import type { FC, ReactNode } from "react";
import { useState } from "react";
import { useMeasure } from "@uidotdev/usehooks";
import styled from "@emotion/styled";
import { colors, fonts, radius, spacing } from "../design-tokens";
import { BodySmall } from "./typography";

const DEFAULT_COLLAPSED_HEIGHT = 120;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md}px;
  width: 100%;
`;

const Container = styled.div<{
  isExpanded: boolean;
  maxHeight: number;
  collapsedHeight: number;
}>`
  position: relative;
  width: 100%;
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  max-height: ${props =>
    props.isExpanded
      ? `${props.maxHeight}px`
      : `${props.collapsedHeight}px`};
`;

const Content = styled.div`
  display: flow-root;
`;

const GradientOverlay = styled.div<{ height: number }>`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: ${props => props.height}px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    color-mix(in srgb, ${colors.surface} 50%, transparent) 20%,
    ${colors.surface} 80%,
    ${colors.surface} 100%
  );
  pointer-events: none;
`;

const ToggleButton = styled.button`
  ${fonts.bodyFont}
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${spacing.md}px ${spacing.lg}px;
  box-sizing: border-box;
  border: 1px solid ${colors.border};
  border-radius: ${radius.md}px;
  background: ${colors.surface};
  color: ${colors.text};
  cursor: pointer;
  transition: background 200ms ease-in-out;

  &:hover {
    background: ${colors.background};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

type CollapsedResponseContainerProps = {
  children: ReactNode;
  shouldCollapseInitially?: boolean;
  collapsedHeight?: number;
  expandLabel?: string;
};

export const CollapsedResponseContainer: FC<
  CollapsedResponseContainerProps
> = ({
  children,
  shouldCollapseInitially = false,
  collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
  expandLabel = "See all results",
}) => {
  const [measureRef, { height }] = useMeasure();
  const [userHasExpanded, setUserHasExpanded] = useState(false);
  const shouldShowReadMore = Boolean(
    shouldCollapseInitially
    && !userHasExpanded
    && height
    && height >= collapsedHeight,
  );

  const handleExpand = () => {
    setUserHasExpanded(true);
  };

  return (
    <Wrapper>
      <Container
        isExpanded={!shouldShowReadMore}
        maxHeight={height || 0}
        collapsedHeight={collapsedHeight}
      >
        <Content ref={measureRef}>{children}</Content>
        {shouldShowReadMore && (
          <GradientOverlay height={Math.min(collapsedHeight * 0.6, 120)} />
        )}
      </Container>
      {shouldShowReadMore && (
        <ToggleButton
          type="button"
          onClick={handleExpand}
          aria-label={expandLabel}
        >
          <BodySmall>{expandLabel}</BodySmall>
        </ToggleButton>
      )}
    </Wrapper>
  );
};
